import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { StorageChangeBusService } from '../services/storage-change-bus.service';

/**
 * Detects successful storage-mutating requests and notifies
 * StorageChangeBusService, so every straw counter in the UI refreshes
 * without any component having to remember it.
 *
 * "Storage-mutating" = POST/PUT/PATCH/DELETE to a backend path that can
 * change what is in the freezer or a straw's state: bank (freeze, thaw,
 * discard, rows, donation, reservation), SEC shipments, straw reservations,
 * PGT cycles, embryo cultivate (freeze/thaw are recorded through bank, but
 * culture edits can move embryos in/out of the storage views) and the
 * sperm laboratory (sperm freeze/thaw are saved as laboratory operations).
 *
 * Only the dependency-free bus is injected here: injecting a service that
 * uses HttpClient from an interceptor creates a DI cycle (NG0200).
 */
@Injectable()
export class StorageMutationInterceptor implements HttpInterceptor {
  private static readonly MUTATING_METHODS = new Set([
    'POST',
    'PUT',
    'PATCH',
    'DELETE'
  ]);

  /**
   * Backend path prefixes (after the API base) whose writes can change
   * straw counts. Keep this list broad: a false positive costs one small
   * GET, a false negative shows a stale counter.
   */
  static readonly STORAGE_PATHS: RegExp =
    /\/(bank|bankStorage|secShipment|strawReservation|pgt|embryoCultivate|laboratory|cellSample|cellsample)(\/|$|\?)/i;

  constructor(private readonly bus: StorageChangeBusService) {}

  /**
   * POST endpoints that only READ (search/list/print/history queries sent
   * as POST). They never change the storage and must not trigger a refresh.
   */
  static readonly READ_ONLY_POSTS: RegExp =
    /\/(search|list|printData|straw-destiny-history|history|summary)(\/|$|\?)|\/search\//i;

  static isStorageMutation(method: string, url: string): boolean {
    return (
      StorageMutationInterceptor.MUTATING_METHODS.has(method.toUpperCase()) &&
      StorageMutationInterceptor.STORAGE_PATHS.test(url) &&
      !StorageMutationInterceptor.READ_ONLY_POSTS.test(url)
    );
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (
      !StorageMutationInterceptor.isStorageMutation(request.method, request.url)
    ) {
      return next.handle(request);
    }
    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.ok) {
          this.bus.notify(request.url);
        }
      })
    );
  }
}
